import { useCustomMutation, useInvalidate } from "@refinedev/core";

interface AddRelationshipParams {
  itemType: string;
  itemId: string;
  relationshipName: string;
  relatedItemId?: string;
  properties?: Record<string, unknown>;
}

interface NotificationConfig {
  message: string;
  type: "success" | "error";
  description?: string;
}

interface UpdateRelationshipParams {
  itemType: string;
  itemId: string;
  relationshipName: string;
  relationshipItemId: string;
  properties: Record<string, unknown>;
  /** Custom success notification. Pass false to disable notification. */
  successNotification?: NotificationConfig | false;
  /** Custom error notification. Pass false to disable notification. */
  errorNotification?: NotificationConfig | false;
}

interface DeleteRelationshipParams {
  itemType: string;
  itemId: string;
  relationshipName: string;
  relationshipItemId: string;
}

interface BulkAddRelationshipParams {
  itemType: string;
  itemId: string;
  relationshipName: string;
  items: Array<{
    relatedId: string;
    properties?: Record<string, unknown>;
  }>;
}

interface RelationshipCrudResult {
  isSuccess: boolean;
  relationshipItemId?: string;
  errorMessage?: string;
  errorCode?: string;
}

interface BulkRelationshipResult {
  isSuccess: boolean;
  totalRequested: number;
  totalCreated: number;
  errorMessage?: string;
}

/**
 * Hook for relationship CRUD mutations (Add, Update, Delete)
 * Note: URL paths should NOT include apiUrl prefix because
 * dataProvider.custom() already prepends it.
 */
export const useRelationshipMutations = () => {
  const invalidate = useInvalidate();

  const addMutation = useCustomMutation<RelationshipCrudResult>();
  const updateMutation = useCustomMutation<RelationshipCrudResult>();
  const deleteMutation = useCustomMutation<RelationshipCrudResult>();
  const bulkAddMutation = useCustomMutation<BulkRelationshipResult>();

  const addRelationshipItem = async (params: AddRelationshipParams) => {
    const { itemType, itemId, relationshipName, relatedItemId, properties } =
      params;

    const body: Record<string, unknown> = { ...properties };
    if (relatedItemId) {
      body.relatedId = relatedItemId;
    }

    return addMutation.mutateAsync({
      url: `/items/${itemType}/${itemId}/relationships/${encodeURIComponent(relationshipName)}`,
      method: "post",
      values: body,
      successNotification: {
        message: "Relationship item added successfully",
        type: "success",
      },
      errorNotification: (error?) => {
        // Extract error message from API response
        const apiMessage =
          (error?.response?.data as { message?: string })?.message ||
          error?.message ||
          "Failed to add relationship item";
        return {
          message: "Failed to add relationship item",
          description: apiMessage,
          type: "error",
        };
      },
    });
  };

  const updateRelationshipItem = async (params: UpdateRelationshipParams) => {
    const {
      itemType,
      itemId,
      relationshipName,
      relationshipItemId,
      properties,
      successNotification: customSuccessNotification,
      errorNotification: customErrorNotification,
    } = params;

    // Use custom notification if provided, otherwise use default
    const successNotification =
      customSuccessNotification !== undefined
        ? customSuccessNotification
        : {
            message: "Relationship item updated successfully",
            type: "success" as const,
          };

    // Use custom notification if provided, otherwise use dynamic error handler
    const errorNotification =
      customErrorNotification !== undefined
        ? customErrorNotification
        : (error?: {
            response?: { data?: { message?: string } };
            message?: string;
          }) => {
            const apiMessage =
              error?.response?.data?.message ||
              error?.message ||
              "Failed to update relationship item";
            return {
              message: "Failed to update relationship item",
              description: apiMessage,
              type: "error" as const,
            };
          };

    return updateMutation.mutateAsync({
      url: `/items/${itemType}/${itemId}/relationships/${encodeURIComponent(relationshipName)}/${relationshipItemId}`,
      method: "put",
      values: properties,
      successNotification,
      errorNotification,
    });
  };

  const deleteRelationshipItem = async (params: DeleteRelationshipParams) => {
    const { itemType, itemId, relationshipName, relationshipItemId } = params;

    return deleteMutation.mutateAsync({
      url: `/items/${itemType}/${itemId}/relationships/${encodeURIComponent(relationshipName)}/${relationshipItemId}`,
      method: "delete",
      values: {},
      successNotification: {
        message: "Relationship item deleted successfully",
        type: "success",
      },
      errorNotification: (error?) => {
        const apiMessage =
          (error?.response?.data as { message?: string })?.message ||
          error?.message ||
          "Failed to delete relationship item";
        return {
          message: "Failed to delete relationship item",
          description: apiMessage,
          type: "error",
        };
      },
    });
  };

  const bulkAddRelationshipItems = async (
    params: BulkAddRelationshipParams,
  ) => {
    const { itemType, itemId, relationshipName, items } = params;

    return bulkAddMutation.mutateAsync({
      url: `/items/${itemType}/${itemId}/relationships/${encodeURIComponent(relationshipName)}/bulk-add`,
      method: "post",
      values: { items },
      successNotification: false,
      errorNotification: (error?) => {
        const apiMessage =
          (error?.response?.data as { message?: string })?.message ||
          error?.message ||
          "Failed to bulk add relationship items";
        return {
          message: "Failed to bulk add relationship items",
          description: apiMessage,
          type: "error",
        };
      },
    });
  };

  const invalidateRelationships = (
    itemType: string,
    itemId: string,
    relationshipName: string,
  ) => {
    // Invalidate the relationship items query
    invalidate({
      resource: `items/${itemType}/${itemId}/relationships/${encodeURIComponent(relationshipName)}`,
      invalidates: ["list"],
    });
  };

  return {
    addRelationshipItem,
    updateRelationshipItem,
    deleteRelationshipItem,
    bulkAddRelationshipItems,
    invalidateRelationships,
    isAddLoading: addMutation.isLoading,
    isUpdateLoading: updateMutation.isLoading,
    isDeleteLoading: deleteMutation.isLoading,
    isBulkAddLoading: bulkAddMutation.isLoading,
    isLoading:
      addMutation.isLoading ||
      updateMutation.isLoading ||
      deleteMutation.isLoading ||
      bulkAddMutation.isLoading,
  };
};

export default useRelationshipMutations;
